import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';

import {
    Checkbox,
    Input,
    Label,
    LabeledControl,
    Select,
    Switch,
    SwitchGroup,
    Flex,
    Text,
    Spacer,
} from '@zillow/constellation';
import {
    Center,
    Col,
    Container,
    Row,
} from '@zillow/zrs-comet';

import {
    DEV_BRAND,
    HOST_APP,
    INSTANT_APP_ENABLED,
    ZG_API,
    ZG_ACH_PAYMENTS_ENABLED,
    ZG_FEED_CONNECT_CREATE_LISTING,
    ZG_APPLICATIONS_VIEWABLE_GROUP,
    ZG_MOCK_USER_EMAIL,
    ZG_MOCK_USER_ENABLED,
    ZG_MOCK_USER_IMPERSONATOR_EMAIL,
    ZG_MOCK_USER_IMPERSONATOR_TOKEN,
    ZG_MOCK_USER_TOKEN,
    SEPARATE_LEASE_CONNECT,
    ZG_RENT_GUARANTEE_ENABLED,
    ZG_ANALYTICS_MARKET_PAGE,
} from 'app/shared/constants/FeatureFlagConstants';
import AppActions from 'app/shared/actions/AppActions';
import AppSelectors from 'app/shared/selectors/AppSelectors';
import EnvSelectors from 'app/shared/selectors/EnvSelectors';
import sharedConfig from 'app/shared/sharedConfig';
import StringConstants from 'app/shared/constants/StringConstants';

import { StyledText } from './StyledComponents';

// **
// ADD NEW FEATURE FLAG TOGGLES HERE
// **

const featureToggles = [
    INSTANT_APP_ENABLED,
    ZG_ACH_PAYMENTS_ENABLED,
    ZG_FEED_CONNECT_CREATE_LISTING,
    SEPARATE_LEASE_CONNECT,
    ZG_RENT_GUARANTEE_ENABLED,
    ZG_ANALYTICS_MARKET_PAGE,
];

const mockUserCookieNames = [
    ZG_MOCK_USER_TOKEN,
    ZG_MOCK_USER_EMAIL,
    ZG_MOCK_USER_IMPERSONATOR_TOKEN,
    ZG_MOCK_USER_IMPERSONATOR_EMAIL,
];

const FeatureFlagPage = () => {
    const dispatch = useDispatch();
    const featureFlags = useSelector(state => AppSelectors.makeCookieEnabledServices(state));
    const zgApi = useSelector(state => EnvSelectors.makeZgApi(state));
    const isProduction = useSelector(state => state.env.isProduction);

    // local state used to control form input value. State keys must be the same as cookie name
    // due to current implementation like `Cookies.set('userid')`.
    const [featureFlagInputState, setFeatureFlagInputState] = useState({
        userId: Cookies.get('userid'),
        ZILLOW_SSID: Cookies.get('ZILLOW_SSID'),
        SESSION_TOKEN: Cookies.get('SESSION_TOKEN'),
        ZG_MOCK_USER_TOKEN: Cookies.get('ZG_MOCK_USER_TOKEN'),
        ZG_MOCK_USER_EMAIL: Cookies.get('ZG_MOCK_USER_EMAIL'),
        ZG_MOCK_USER_IMPERSONATOR_TOKEN: Cookies.get('ZG_MOCK_USER_IMPERSONATOR_TOKEN'),
        ZG_MOCK_USER_IMPERSONATOR_EMAIL: Cookies.get('ZG_MOCK_USER_IMPERSONATOR_EMAIL'),
    });

    const isZillowDevBrand = featureFlags[DEV_BRAND] === sharedConfig.brands.zillow;
    const selects = [{
        name: DEV_BRAND,
        values: Object.values(StringConstants.UNFORMATTED_BRAND_NAMES),
        isDisabled: isProduction,
    }, {
        name: ZG_API,
        values: [...new Set([zgApi, ...Object.values(sharedConfig.zgApi)])],
        isDisabled: isProduction,
    }, {
        name: ZG_APPLICATIONS_VIEWABLE_GROUP,
        values: ['none', 'all'],
        isDisabled: false,
    }, {
        name: HOST_APP,
        values: ['none', 'ios', 'android'],
        isDisabled: false,
    }];

    const handleChange = (name, value) => {
        dispatch(AppActions.updateFeatureFlag(name, value));
        if (name === DEV_BRAND) {
            window.location.reload();
        }
    };

    const renderToggles = toggles => (
        <SwitchGroup size="lg">
            {toggles.map((feature => (
                <LabeledControl
                    key={feature}
                    controlId={`SwitchGroup-${feature}`}
                    label={<Label>{feature}</Label>}
                    control={(
                        <Switch
                            checkbox={(
                                <Checkbox
                                    checked={featureFlags[feature]}
                                    onClick={() => handleChange(feature, !featureFlags[feature])}
                                />
                            )}
                        />
                    )}
                />
            )))}
        </SwitchGroup>
    );

    const updateCookie = (name, value) => {
        // Custom converter to override default encoding behavior for special characters `%`
        // https://github.com/js-cookie/js-cookie#encoding
        Cookies.withConverter({ write: _ => _ }).set(name, value);
        setFeatureFlagInputState({ ...featureFlagInputState, [name]: value });
    };

    const renderInputs = inputs => (
        inputs.map(name => (
            <Spacer marginY="sm">
                <Flex display="flex" alignItems="center">
                    <StyledText>{name}</StyledText>
                    <Input
                        value={featureFlagInputState[name]}
                        onChange={({ target: { value } }) => updateCookie(name, value)}
                    />
                </Flex>
            </Spacer>
        )));

    const renderSelects = () => (
        selects.map(({ name, values, isDisabled }) => {
            if (typeof Cookies.get(name) === 'undefined') {
                updateCookie(name, featureFlags[name]);
            }

            return (
                <Spacer marginY="sm" key={name}>
                    <Label>{name}</Label>
                    <Select
                        value={featureFlags[name]}
                        onChange={e => handleChange(name, e.target.value)}
                        disabled={isDisabled}
                    >
                        {values.map(value => (<option key={value} value={value}>{value}</option>))}
                    </Select>
                </Spacer>
            );
        }));


    return (
        <Container className="FeatureFlagPage">
            <Row>
                <Col sm={0} md={0} lg={2} />
                <Col sm={12} md={12} lg={8}>
                    {renderSelects()}
                    <Spacer marginY="sm">
                        <Center><b>Cookies</b></Center>
                    </Spacer>
                    {renderInputs(isZillowDevBrand ? ['userid', 'ZILLOW_SSID'] : ['SESSION_TOKEN'])}
                    <Spacer marginY="sm">
                        <Center><b>Features</b></Center>
                    </Spacer>
                    <Spacer marginY="sm">
                        <Flex display="flex" alignItems="center" justifyContent="space-between">
                            <Text><b>Feature name</b></Text>
                            <Text><b>Feature status</b></Text>
                        </Flex>
                    </Spacer>
                    {renderToggles(featureToggles)}
                    <Spacer marginY="sm">
                        <Center><b>Mock user</b></Center>
                    </Spacer>
                    {renderToggles([ZG_MOCK_USER_ENABLED])}
                    {renderInputs(mockUserCookieNames)}
                </Col>
                <Col sm={0} md={0} lg={2} />
            </Row>
        </Container>
    );
};


export default FeatureFlagPage;
